import React, { useState, useRef } from 'react';
import { imgUrl } from '../utils/imgUrl';

/**
 * ZoomImage — hover-to-zoom image wrapper.
 * On desktop hover, the cursor position pans a magnified version of the image inside the same frame.
 * On touch / mobile, behaves like a regular image (no zoom on tap to avoid accidental activations).
 *
 * Two layout modes:
 *  - default (fill): the image stretches to fill the container (use for fixed-size frames).
 *  - natural: the image uses its own height (h-auto). Best for spec/diagram images.
 */
export const ZoomImage = ({
  src,
  alt = '',
  className = '',
  imgClassName = '',
  objectFit = 'cover', // 'cover' | 'contain'
  zoom = 2,
  natural = false,
  loading,
  testId,
}) => {
  const containerRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  const handleMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const imgStyle = {
    transform: isZoomed ? `scale(${zoom})` : 'scale(1)',
    transformOrigin: `${origin.x}% ${origin.y}%`,
  };

  if (!natural) {
    imgStyle.objectFit = objectFit;
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsZoomed(true)}
      onMouseLeave={() => setIsZoomed(false)}
      onMouseMove={handleMove}
      style={{ cursor: isZoomed ? 'zoom-out' : 'zoom-in' }}
      data-testid={testId}
    >
      <img
        src={imgUrl(src)}
        alt={alt}
        loading={loading}
        decoding="async"
        className={`${natural ? 'w-full h-auto' : 'w-full h-full'} transition-transform duration-200 ease-out ${imgClassName}`}
        style={imgStyle}
        draggable={false}
      />
    </div>
  );
};

export default ZoomImage;
